import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { InputText } from '#app/components/ui/input-text'

interface ProductoData {
	valorProducto: string
	listadoProductos: string
	descripcionProducto: string
	historialSeguros: string
}

interface RCProductosProductoScreenProps {
	onNext: (data: ProductoData) => void
	onBack: () => void
	data?: Partial<ProductoData>
}

export function RCProductosProductoScreen({
	onNext,
	onBack,
	data,
}: RCProductosProductoScreenProps) {
	const [formData, setFormData] = useState<ProductoData>({
		valorProducto: data?.valorProducto || '',
		listadoProductos: data?.listadoProductos || '',
		descripcionProducto: data?.descripcionProducto || '',
		historialSeguros: data?.historialSeguros || '',
	})

	const [errors, setErrors] = useState<
		Partial<Record<keyof ProductoData, string>>
	>({})

	const validateForm = (): boolean => {
		const newErrors: Partial<Record<keyof ProductoData, string>> = {}

		if (!formData.listadoProductos.trim()) {
			newErrors.listadoProductos = 'El listado de productos es requerido'
		}
		if (!formData.descripcionProducto.trim()) {
			newErrors.descripcionProducto = 'La descripción del producto es requerida'
		}

		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleSubmit = () => {
		if (validateForm()) {
			onNext(formData)
		}
	}

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-6">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Información del Producto
					</h2>
					<p className="text-center text-sm text-neutral-600">
						Detallá los productos que elabora tu empresa
					</p>

					<div className="flex w-full flex-col gap-4">
						<InputText
							label="Valor Unitario del Producto (opcional)"
							labelPosition="internal"
							size="l"
							placeholder="Ej: $5.000"
							value={formData.valorProducto}
							onChange={(e) =>
								setFormData({ ...formData, valorProducto: e.target.value })
							}
						/>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Listado de Productos Manufacturados
							</label>
							<p className="text-xs text-neutral-500">
								Listá todos los productos que elabora tu empresa
							</p>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-base"
								rows={4}
								placeholder="Ej: Alimentos enlatados, conservas, productos congelados, etc."
								value={formData.listadoProductos}
								onChange={(e) =>
									setFormData({ ...formData, listadoProductos: e.target.value })
								}
							/>
							{errors.listadoProductos && (
								<p className="text-sm text-red-500">
									{errors.listadoProductos}
								</p>
							)}
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Descripción del Producto
							</label>
							<p className="text-xs text-neutral-500">
								Componentes, materiales, uso previsto y características
							</p>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-base"
								rows={4}
								placeholder="Describí los componentes principales, materiales utilizados y el uso previsto de los productos"
								value={formData.descripcionProducto}
								onChange={(e) =>
									setFormData({
										...formData,
										descripcionProducto: e.target.value,
									})
								}
							/>
							{errors.descripcionProducto && (
								<p className="text-sm text-red-500">
									{errors.descripcionProducto}
								</p>
							)}
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Historial de Seguros y Siniestros (opcional)
							</label>
							<p className="text-xs text-neutral-500">
								¿Tuvo seguros anteriores? ¿Hubo siniestros o reclamos?
							</p>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-base"
								rows={3}
								placeholder="Ej: Seguro RC con Compañía X durante 5 años. Sin siniestros declarados."
								value={formData.historialSeguros}
								onChange={(e) =>
									setFormData({ ...formData, historialSeguros: e.target.value })
								}
							/>
						</div>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Siguiente
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
